import {
  createRef,
  useEffect,
  useRef,
  useState
} from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import { setCookie, getCookie } from '../../util/cookies.js';
import LdsIcon from '../LdsIcon/index.jsx';
import SelectOption from '../LdsSelect/SelectOption.jsx';

function LdsLanguageSelect({
  id,
  name,
  label = '',
  defaultValue,
  options,
  inputProps,
  onChange = () => {},
  className,
  ldsTrackingCookieName = 'lds_allowTracking',
  ldsLanguageCookieName = 'lds_selected_language',
  storeLanguageCookie = false, // prop only to be used on sites that do not need a cookie banner
  ...rest
}) {
  const selectRef = useRef(null);
  const optionsRef = useRef(options.map(() => createRef()));

  // This is considered a preference cookie and will not be set if the user has not accepted cookies
  const setSiteLanguageCookie = (value) => {
    const cookieAcceptance = getCookie(ldsTrackingCookieName);
    if (cookieAcceptance === 'enabled' || storeLanguageCookie) {
      setCookie(ldsLanguageCookieName, value, 365);
    }
  };

  const selectDefaultItem = () => {
    if (!options || !Array.isArray(options) || options.length < 1) return { value: undefined, label: undefined };

    if (defaultValue === undefined || defaultValue === null) {
      return options[0];
    }

    const foundItem = options.find((option) => option.value === defaultValue);
    if (!foundItem) {
      // eslint-disable-next-line no-console
      console.error(`
      LdsLanguageSelect - no corresponding option provided for defaultValue: ${defaultValue}
        type: ${typeof defaultValue}
      `);
    }
    return foundItem;
  };

  const setDefaultAriaDescribedBy = (defaultValue) => {
    if (!options || !Array.isArray(options) || options.length < 1) return '';

    if (defaultValue === undefined || defaultValue === null) {
      return '';
    }

    const foundItemIdx = options.findIndex((option) => option.value === defaultValue);
    return `${id}-option-btn-${foundItemIdx}`;
  };

  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(selectDefaultItem(defaultValue));
  const [focusedOption, setFocusedOption] = useState(selectDefaultItem(defaultValue));
  const [ariaDescribedBy, setAriaDescribedBy] = useState(setDefaultAriaDescribedBy(defaultValue));

  useEffect(() => {
    setSelectedItem(selectDefaultItem(defaultValue));
    setFocusedOption(selectDefaultItem(defaultValue));
    setAriaDescribedBy(setDefaultAriaDescribedBy(defaultValue));
  }, [defaultValue]);

  const handleOpen = () => {
    setIsOpen(true);
    setFocusedOption(selectedItem);
  };

  useEffect(() => {
    // focus on the active item when opened
    if (isOpen) {
      const activeIdx = options.findIndex((option) => option.value === selectedItem.value);
      optionsRef.current[activeIdx].current.focus();
    }
  }, [isOpen, options, selectedItem.value]);

  const handleSelectClick = () => {
    handleOpen();
  };

  const handleOptionBlur = (e) => {
    let isFocused = false;
    if (e?.relatedTarget?.matches('.lds-select-option')) isFocused = true;

    if (!isFocused) setIsOpen(false);
  };

  const handleSelectKeyDown = (e) => {
    const code = e.code;

    // open the dropdown and switch the focus to the selected item
    if (code === 'Space') {
      handleOpen();
    }

    // Tab away from the select
    if (code === 'Tab') {
      setIsOpen(false);
    }
  };

  const handleOptionKeyDown = (e) => {
    const isUp = e.code === 'ArrowUp';
    const isDown = e.code === 'ArrowDown';

    if (e.code === 'Escape') {
      setIsOpen(false);
      selectRef.current.focus();
    }

    if (isUp) {
      const activeIdx = options.findIndex((option) => option.value === focusedOption.value);
      if (activeIdx === 0) return;

      const newIdx = activeIdx - 1;
      optionsRef.current[newIdx].current.focus();
      setFocusedOption(options[newIdx]);
    }

    if (isDown) {
      const activeIdx = options.findIndex((option) => option.value === focusedOption.value);
      if (activeIdx === options.length - 1) return;

      const newIdx = activeIdx + 1;
      optionsRef.current[newIdx].current.focus();
      setFocusedOption(options[newIdx]);
    }
  };

  const handleSelectOption = (option, optionId) => {
    setAriaDescribedBy(optionId);
    setSelectedItem(option);
    setIsOpen(false);
    selectRef.current.focus();

    setSiteLanguageCookie(option.value);
    // Call passed in onChange event
    onChange(option);
  };

  return (
    <div
      className={cn(
        'lds-language-select-wrapper',
        className
      )}
      {...rest}
    >
      { label
        && <label
          className={
            'lds-form-label'
          }
          htmlFor={`${id}-button`}
        >
          {label}
        </label>
      }

      <div
        className={cn(
          'lds-select-input-wrapper',
          {
            open: isOpen,
          }
        )}
      >
        <input
          type="hidden"
          id={id}
          name={name}
          value={selectedItem?.value}
          {...inputProps}
        ></input>
        <button
          className={cn(
            'button-clear-style',
            'lds-select',
            {
              open: isOpen,
            }
          )}
          onClick={handleSelectClick}
          onKeyDown={handleSelectKeyDown}
          tabIndex="0"
          id={`${id}-button`}
          ref={selectRef}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-describedby={ariaDescribedBy}
        >
          <LdsIcon className="globe" name="Globe" aria-hidden="true" />
          {selectedItem?.label}
          <LdsIcon className="caret-down" name="CaretDown" aria-hidden="true" />
        </button>
        <div
          className="lds-select-options"
          role="listbox"
          aria-hidden={!isOpen}
        >
          {options
            && Array.isArray(options)
            && options.map((option, idx) => (
              <SelectOption
                key={idx}
                option={option}
                idx={idx}
                optionsRef={optionsRef}
                id={id}
                handleSelectOption={handleSelectOption}
                handleOptionBlur={handleOptionBlur}
                handleOptionKeyDown={handleOptionKeyDown}
                selectedItem={selectedItem}
              />
            ))}
        </div>
      </div>
    </div>
  );
}

LdsLanguageSelect.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  inputProps: PropTypes.object,
  options: PropTypes.arrayOf((propValue, key, componentName, propFullName) => { // eslint-disable-line
    if (!propValue[key].value) {
      return new Error(
        `Invalid prop ${propFullName} supplied to ${componentName} at index ${key}. Each option must contain a "value" property.`
      );
    }
    if (!propValue[key].label) {
      return new Error(
        `Invalid prop ${propFullName} supplied to ${componentName} at index ${key}. Each option must contain a "label" property.`
      );
    }
  }),
};

export default LdsLanguageSelect;
